import { useState, useEffect, useRef } from 'react';

/**
 * Like useState, but persisted to localStorage under `key`, and synced across
 * every component in the same tab that uses the same key.
 *
 * The browser's native `storage` event only fires across tabs, not within one,
 * so we dispatch a custom event ourselves whenever a value is written. Any
 * other useStickyState(key) subscriber in this tab updates its local state.
 *
 * IMPORTANT: persistence + broadcast happen in a useEffect so they run after
 * the render commit. Doing it inside the setValue updater (the previous
 * version) caused subscribers to miss updates because dispatchEvent fired
 * during another component's render.
 */
const SYNC_EVENT = 'sticky-state:sync';

export default function useStickyState(key, defaultValue, isValid) {
  // Optional `isValid(value)` callback — return false to discard the persisted
  // value and fall back to defaultValue. Use this when you change a set of
  // allowed options (e.g. add/remove a market) so old keys can't strand a
  // user on a no-longer-supported value.
  const validate = (v) => (typeof isValid === 'function' ? !!isValid(v) : true);

  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      const parsed = JSON.parse(stored);
      return validate(parsed) ? parsed : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const isFirstCommit = useRef(true);

  // Persist + broadcast after every render that changed `value`. Skipping the
  // first commit avoids re-broadcasting the value we just hydrated from
  // localStorage on mount.
  useEffect(() => {
    if (isFirstCommit.current) {
      isFirstCommit.current = false;
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify(value));
      window.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: { key, value } }));
    } catch {
      /* localStorage full / disabled — UI still works, just won't persist */
    }
  }, [key, value]);

  // Listen for updates from other consumers in this tab (custom event) and
  // from other tabs (native `storage` event).
  useEffect(() => {
    const handleSync = (e) => {
      if (e?.detail?.key !== key) return;
      const v = e.detail.value;
      setValue(validate(v) ? v : defaultValue);
    };
    const handleStorage = (e) => {
      if (e.key !== key) return;
      try {
        const parsed = e.newValue !== null ? JSON.parse(e.newValue) : defaultValue;
        setValue(validate(parsed) ? parsed : defaultValue);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener(SYNC_EVENT, handleSync);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(SYNC_EVENT, handleSync);
      window.removeEventListener('storage', handleStorage);
    };
  }, [key, defaultValue]);

  return [value, setValue];
}
